import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';

/**
 * Service for loading and transforming data in service detail view
 * Centralizes complex data loading operations
 */
@Injectable()
export class ServiceDetailDataLoaderService {

    constructor() { }

    /**
     * Parse JSON fields in origin points (puntos_origen)
     * @param puntosOrigen - Array of origin points
     * @returns Array with parsed JSON fields
     */
    parsePuntosOrigenJsonFields(puntosOrigen: any[]): any[] {
        return puntosOrigen.map(value => {
            const parsed = { ...value };

            if (parsed.adicionales) {
                parsed.adicionales = JSON.parse(parsed.adicionales);
            }
            if (parsed.camionetas) {
                parsed.camionetas = JSON.parse(parsed.camionetas);
            }
            if (parsed.costo_operador) {
                parsed.costo_operador = JSON.parse(parsed.costo_operador);
            }

            return parsed;
        });
    }

    /**
     * Find selected origin point based on office ID
     * @param puntosOrigen - Parsed origin points array
     * @param oficinaDeOrigen - Office ID to match
     * @returns Selected origin point or empty object
     */
    findPuntoOrigenSeleccionado(puntosOrigen: any[], oficinaDeOrigen: string): any {
        const found = puntosOrigen.find(value => value.id_punto_origen === oficinaDeOrigen);
        return found || { adicionales: [] };
    }

    /**
     * Transform notes changes array for display
     * @param notasCambio - Raw notes array
     * @returns Sorted notes array
     */
    transformNotasCambios(notasCambio: any[]): any[] {
        const transformed = notasCambio.map(n_c => {
            if (n_c.nota) {
                const date = new Date(n_c.fecha);
                return {
                    ...n_c,
                    formattedDate: date.toString()
                };
            }
            return n_c;
        });

        transformed.sort((a, b) => b.fecha - a.fecha);

        return transformed;
    }

    /**
     * Build formatted string of all notes changes
     * @param notasCambio - Notes array
     * @returns Formatted string or "SIN CAMBIOS"
     */
    buildNotasCambiosString(notasCambio: any[]): string {
        let notasCambioStr = "";

        notasCambio.forEach(n_c => {
            if (n_c.nota) {
                const date = new Date(n_c.fecha);
                notasCambioStr += n_c.nota + " - Hecho en: " + date.toString() + ". ";
            }
        });

        return notasCambioStr !== "" ? notasCambioStr : "SIN CAMBIOS";
    }

    /**
     * Load contact ID links for all three contact types
     * @param contactosService - ContactosService instance
     * @param contactos - Contact IDs object
     * @returns Observable with all three ID links
     */
    loadContactIdLinks(contactosService: any, contactos: any): Observable<any> {
        const usuario$ = contactosService
            .obtenerPropiedadContacto(contactos.usuario, "id_link")
            .valueChanges()
            .take(1);

        const coordinador$ = contactosService
            .obtenerPropiedadContacto(contactos.coordinador, "id_link")
            .valueChanges()
            .take(1);

        const contratante$ = contactosService
            .obtenerPropiedadContacto(contactos.contratante, "id_link")
            .valueChanges()
            .take(1);

        return Observable.forkJoin(
            usuario$,
            coordinador$,
            contratante$
        ).map(([usuario, coordinador, contratante]) => ({
            usuario,
            coordinador,
            contratante
        }));
    }

    /**
     * Extract service ID and parameters from confirmacion servicios
     * @param confirmacion - Confirmation object
     * @returns Object with service ID and parameters
     */
    extractServicioConfirmacionData(confirmacion: any): any {
        let id_servicio_confirmacion = null;
        let parametros_confirmacion = null;

        for (const idServicio in confirmacion.servicios) {
            if (confirmacion.servicios.hasOwnProperty(idServicio)) {
                id_servicio_confirmacion = idServicio;
                parametros_confirmacion = confirmacion.servicios[idServicio].parametros;
                break;
            }
        }

        return {
            id_servicio_confirmacion,
            parametros_confirmacion
        };
    }

    /**
     * Map viajes snapshot to array with $key
     * @param viajesSnapshot - Firestore snapshot
     * @returns Array of viajes with $key
     */
    mapViajesSnapshot(viajesSnapshot: any[]): any[] {
        const arr = [];
        viajesSnapshot.forEach(viaje => {
            const $key = viaje.payload.doc.id;
            const data = viaje.payload.doc.data();
            arr.push({ $key, ...data });
        });
        return arr;
    }

    /**
     * Map contactos snapshot to array with $key
     * @param contactosSnapshot - Firebase snapshot
     * @returns Array of contactos with $key
     */
    mapContactosSnapshot(contactosSnapshot: any[]): any[] {
        const contactosArr = [];
        contactosSnapshot.forEach(contacto => {
            const $key = contacto.payload.key;
            const data = { $key, ...contacto.payload.val() };
            contactosArr.push(data);
        });
        return contactosArr;
    }

    /**
     * Initialize descuentos object with confirmacion data
     * @param confirmacion - Confirmation object
     * @returns Initialized descuentos object
     */
    initializeDescuentosObj(confirmacion: any): any {
        return {
            subtotal: confirmacion.subtotal,
            iva: confirmacion.iva,
            total: confirmacion.total,
            codigoMarketing: '',
            codigoAdmin: '',
            descuentoAdicional: {
                cantidad: 0,
                razon: ""
            }
        };
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/service-detail-data-loader.service.ts